<template>
  <nav>
    <ul class="flex justify-center gap-8">
      <li v-for="tab in appTabs" :key="tab.app">
        <button
          type="button"
          class="text-static-white-80 relative flex flex-col items-center justify-center gap-1 text-center text-xs font-medium"
          :data-qa-id="tab.qaId"
          @click="switchApp(tab.app)"
        >
          <span
            class="relative block size-14 backdrop-blur-xs [clip-path:inset(0_round_16px)]"
          >
            <HotelsSwitchIcon
              v-if="tab.app === FlowType.HOTELS"
              :active="activeApp === tab.app"
              :pulse-key="hotelsPlay"
            />
            <component
              v-else
              :is="activeApp === tab.app ? tab.activeIcon : tab.inactiveIcon"
              class="block size-14"
            />
            <!-- Rays sit above the icon but inside the clip — trimmed by the backdrop -->
            <HotelsRays
              v-if="tab.app === FlowType.HOTELS"
              :pulse-key="hotelsPlay"
              class="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            />
          </span>
          <HotelsNewBadge
            v-if="tab.app === FlowType.HOTELS"
            :pulse-key="hotelsPlay"
          />
          <span class="block">{{ tab.label }}</span>
        </button>
      </li>
    </ul>
  </nav>
</template>

<script setup lang="ts">
  import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  import messages from './config/locales.json';
  import { FlowType } from '../../shared/config';
  import HotelsSwitchIcon from './HotelsSwitchIcon.vue';
  import HotelsRays from './HotelsRays.vue';
  import HotelsNewBadge from './HotelsNewBadge.vue';
  import AviaActiveIcon from './assets/avia-active.svg?component';
  import AviaInactiveIcon from './assets/avia-inactive.svg?component';
  import RwActiveIcon from './assets/rw-active.svg?component';
  import RwInactiveIcon from './assets/rw-inactive.svg?component';
  import ToursActiveIcon from './assets/tours-active.svg?component';
  import ToursInactiveIcon from './assets/tours-inactive.svg?component';
  import HotelsActiveIcon from './assets/hotels-active.svg?component';
  import HotelsInactiveIcon from './assets/hotels-inactive.svg?component';

  const props = defineProps<{ activeApp: FlowType; sourcePage?: string }>();
  const emit = defineEmits<{
    onMiniAppSwitch: [app: FlowType];
  }>();

  const { t } = useI18n({ messages });

  const appTabs = computed(() => [
    {
      app: FlowType.AVIA,
      qaId: `${props.sourcePage}.appSwitch.avia`,
      label: t('appSwitchButton.avia'),
      activeIcon: AviaActiveIcon,
      inactiveIcon: AviaInactiveIcon,
    },
    {
      app: FlowType.RAILWAYS,
      qaId: `${props.sourcePage}.appSwitch.railways`,
      label: t('appSwitchButton.railways'),
      activeIcon: RwActiveIcon,
      inactiveIcon: RwInactiveIcon,
    },
    {
      app: FlowType.TOURS,
      qaId: `${props.sourcePage}.appSwitch.tours`,
      label: t('appSwitchButton.tours'),
      activeIcon: ToursActiveIcon,
      inactiveIcon: ToursInactiveIcon,
    },
    {
      app: FlowType.HOTELS,
      qaId: `${props.sourcePage}.appSwitch.hotels`,
      label: t('appSwitchButton.hotels'),
      activeIcon: HotelsActiveIcon,
      inactiveIcon: HotelsInactiveIcon,
    },
  ]);

  // Shared play counter for the «Hotels» animation — drives the icon, rays and badge
  // in sync. Each increment = one playthrough (click or autoplay tick).
  const hotelsPlay = ref(0);

  // Autoplay without interaction: first run shortly after mount, then every 5s.
  const AUTOPLAY_INTERVAL = 5000;
  let firstTimer: ReturnType<typeof setTimeout>;
  let loopTimer: ReturnType<typeof setInterval>;
  onMounted(() => {
    firstTimer = setTimeout(() => (hotelsPlay.value += 1), 800);
    loopTimer = setInterval(() => (hotelsPlay.value += 1), AUTOPLAY_INTERVAL);
  });
  onBeforeUnmount(() => {
    clearTimeout(firstTimer);
    clearInterval(loopTimer);
  });

  function switchApp(appName: FlowType) {
    /** To prevent reopening active app */
    const isTryingToOpenActiveApp = appName === props.activeApp;

    if (appName === FlowType.HOTELS) {
      hotelsPlay.value += 1; // play the animation (even on the already-active tab)
      if (!isTryingToOpenActiveApp) {
        // let the macro animation play out, then switch
        window.setTimeout(() => emit('onMiniAppSwitch', appName), 420);
      }
      return;
    }

    if (!isTryingToOpenActiveApp) {
      emit('onMiniAppSwitch', appName);
    }
  }
</script>
